import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  AlertTriangle,
  Building2,
  Search,
  Users,
  FileText,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import AgencyClassificationBadge from '../../components/agency/AgencyClassificationBadge';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Agency List Page - Phase 8
 * 
 * Platform owner view of all agencies with search and filtering.
 */
const AgencyListPage = () => {
  const { token, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agencies, setAgencies] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoadingAgencies, setIsLoadingAgencies] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const statusFilter = searchParams.get('status') || 'all';
  // Monotonic id so late responses from superseded requests are ignored.
  const requestIdRef = useRef(0);

  const fetchAgencies = async (overrides = {}) => {
    const requestId = ++requestIdRef.current;
    setIsLoadingAgencies(true);
    setError(null);
    try {
      const search = overrides.search !== undefined ? overrides.search : searchTerm;
      const status = overrides.status !== undefined ? overrides.status : statusFilter;
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (status !== 'all') params.append('status', status);
      params.append('limit', '50');

      const response = await axios.get(`${API}/owner-console/agencies?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (requestId !== requestIdRef.current) return;
      setAgencies(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('Failed to fetch agencies:', err);
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : typeof detail?.message === 'string'
            ? detail.message
            : 'Failed to load agencies';
      setError(message);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingAgencies(false);
      }
    }
  };

  useEffect(() => {
    if (authLoading) return undefined;
    if (token) {
      fetchAgencies();
    } else {
      // No usable session: never leave the skeletons up forever.
      setIsLoadingAgencies(false);
      setError('Your session is not available. Please retry or sign in again.');
    }
    const onClassificationUpdated = (event) => {
      const updatedId = event?.detail?.agencyId;
      const classification = event?.detail?.classification;
      if (!updatedId || !classification) {
        fetchAgencies();
        return;
      }
      setAgencies((prev) =>
        prev.map((agency) =>
          agency.agency_id === updatedId
            ? {
                ...agency,
                agency_type: classification.agency_type,
                current_plan: classification.plan_id || classification.current_plan,
                primary_product: classification.primary_product || classification.product_type,
                classification: classification.classification || null,
              }
            : agency
        )
      );
    };
    window.addEventListener('fundelite:agency-classification-updated', onClassificationUpdated);
    return () => {
      // Invalidate in-flight requests on unmount / dependency change.
      requestIdRef.current += 1;
      window.removeEventListener('fundelite:agency-classification-updated', onClassificationUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, token, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAgencies();
  };

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    setSearchParams(newParams);
  };

  const hasActiveFilters = Boolean(searchTerm) || statusFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    newParams.delete('status');
    setSearchParams(newParams);
    fetchAgencies({ search: '', status: 'all' });
  };

  return (
    <div className="space-y-6" data-testid="agency-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Agencies</h1>
          <p className="text-slate-500 mt-1">Manage and support platform agencies</p>
        </div>
        <Button
          onClick={() => fetchAgencies()}
          variant="outline"
          size="sm"
          disabled={isLoadingAgencies}
          data-testid="agencies-refresh"
        >
          <RefreshCw size={16} className={`mr-2 ${isLoadingAgencies ? 'animate-spin' : ''}`} />
          {isLoadingAgencies ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <form onSubmit={handleSearch} className="flex-1 min-w-[200px] relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search agencies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </form>
        <Select value={statusFilter} onValueChange={(v) => updateFilter('status', v)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Active &amp; other (excludes deleted)</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending_deletion">Pending Deletion</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats — only once loading has finished */}
      {!isLoadingAgencies && !error && (
        <div className="flex items-center gap-4 text-sm text-slate-500">
          <span data-testid="agencies-count-label">
            Showing {agencies.length} of {total} agencies
          </span>
        </div>
      )}

      {/* Agencies Grid */}
      {isLoadingAgencies ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              data-testid="agency-skeleton-card"
              className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-slate-200" />
                <div className="flex-1">
                  <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-slate-100 rounded" />
                <div className="h-3 bg-slate-100 rounded w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div
          className="bg-white rounded-xl border border-slate-200 p-12 text-center"
          data-testid="agencies-error-state"
        >
          <AlertTriangle size={48} className="mx-auto text-rose-400 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">Unable to load agencies</h3>
          <p className="text-slate-500 mb-6">
            Something went wrong while loading agencies. Please try again.
          </p>
          <Button
            variant="outline"
            onClick={() => fetchAgencies()}
            data-testid="agencies-retry"
          >
            <RefreshCw size={16} className="mr-2" />
            Retry
          </Button>
        </div>
      ) : agencies.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agencies.map((agency) => (
            <Link
              key={agency.agency_id}
              to={`/platform/agencies/${agency.agency_id}`}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-amber-300 transition-all"
              data-testid={`agency-card-${agency.agency_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                    <Building2 className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">{agency.name || 'Unnamed Agency'}</h3>
                    <p className="text-xs text-slate-500 font-mono">{agency.agency_id}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  agency.status === 'pending_deletion'
                    ? 'bg-rose-100 text-rose-700'
                    : agency.status === 'active'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-600'
                }`}>
                  {agency.status === 'pending_deletion' ? 'Pending Deletion' : (agency.status || 'active')}
                </span>
              </div>
              <div className="mb-3">
                <AgencyClassificationBadge
                  agency={agency}
                  testId={`agency-type-badge-${agency.agency_id}`}
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Users size={16} className="text-slate-400" />
                  <span className="text-slate-700">{agency.user_count || 0} users</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FileText size={16} className="text-slate-400" />
                  <span className="text-slate-700">{agency.case_count || 0} cases</span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <span className="text-xs text-slate-400">
                  Created: {agency.created_at ? new Date(agency.created_at).toLocaleDateString() : 'N/A'}
                </span>
                <ArrowRight size={16} className="text-slate-400" />
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div
          className="bg-white rounded-xl border border-slate-200 p-12 text-center"
          data-testid="agencies-empty-state"
        >
          <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No agencies found</h3>
          <p className="text-slate-500">
            {hasActiveFilters
              ? 'No agencies match the current search or status filter.'
              : 'No agencies in the platform yet'}
          </p>
          {hasActiveFilters && (
            <Button
              variant="outline"
              className="mt-6"
              onClick={clearFilters}
              data-testid="agencies-clear-filters"
            >
              Clear filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AgencyListPage;
